import React, { useState, useEffect } from "react";
import {
  Drawer,
  Box,
  IconButton,
  Typography,
  Grid,
  Chip,
  TextField,
  Button,
  useTheme,
  useMediaQuery,
  Alert,
  Stack,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import Spinner from "../../layout/spinner";
import { updateCourseAccessCode, getCourseAccessCode } from "../../../services/apiCourses";

const AccessCodeDrawer = ({ openDrawer, setOpenDrawer, dataDrawer, onSuccess }) => {
  const [accessCode, setAccessCode] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    let isMounted = true;
    const fetchCourseData = async () => {
      if (openDrawer && dataDrawer?.courseId) {
        setIsLoading(true);
        setErrorMsg("");
        try {
          const response = await getCourseAccessCode(dataDrawer.courseId);
          if (isMounted && response?.status === 200) {
            const resData = response.data;
            setAccessCode(resData?.data || "");
          } else if (isMounted) {
            setAccessCode(dataDrawer.saccesscode || dataDrawer.accessCode || "");
          }
        } catch (e) {
          if (isMounted) {
            setAccessCode(dataDrawer.saccesscode || dataDrawer.accessCode || "");
          }
        } finally {
          if (isMounted) {
            setIsLoading(false);
          }
        }
      }
    };
    fetchCourseData();
    return () => {
      isMounted = false;
    };
  }, [openDrawer, dataDrawer]);

  const handleSave = async () => {
    setIsLoading(true);
    setErrorMsg("");
    try {
      const response = await updateCourseAccessCode(dataDrawer.courseId, accessCode);
      if (response && (response.status === 200 || response.status === 204 || (response.data && response.data.isValid))) {
        onSuccess("Código actualizado correctamente");
        setOpenDrawer(false);
      } else {
        const resData = response?.data;
        if (resData && !resData.isValid && resData.errors && resData.errors.length > 0) {
           const firstError = resData.errors[0];
           setErrorMsg(firstError.errorMessage || "Error al actualizar el código.");
        } else {
           setErrorMsg("Error inesperado al comunicarse con el servidor.");
        }
      }
    } catch (error) {
      // El interceptor de axios en apiBase.js hace `throw response;`
      // por lo que 'error' es en realidad el objeto response
      const resData = error.data || error.response?.data;
      if (resData && !resData.isValid && resData.errors && resData.errors.length > 0) {
        const firstError = resData.errors[0];
        setErrorMsg(firstError.errorMessage || "Error al comunicarse con el servidor.");
      } else {
        setErrorMsg("Error inesperado al comunicarse con el servidor.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Drawer
      anchor={isMobile ? "bottom" : "right"}
      open={openDrawer}
      PaperProps={{
        sx: {
          zIndex: 1203,
          width: { xs: "100vw", sm: 500 },
          maxWidth: "100%",
          maxHeight: { xs: "90vh", sm: "100%" },
          borderRadius: { xs: "12px 12px 0 0", sm: 0 },
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
        },
      }}
      onClose={() => setOpenDrawer(false)}
      sx={{ zIndex: 1203, flexShrink: 0 }}
    >
      <Box
        sx={{
          flexShrink: 0,
          position: "relative",
          px: { xs: 2, sm: 3 },
          pt: { xs: 2, sm: 4 },
          pb: { xs: 1.5, sm: 2 },
          backgroundColor: "primary.main", // Igual que el de profesores (suponiendo que es 'primary.main' si se pasaba 'primary')
        }}
      >
        <IconButton
          sx={{
            position: "absolute",
            top: { xs: 8, sm: 16 },
            right: { xs: 8, sm: 16 },
            color: "white",
          }}
          onClick={() => setOpenDrawer(false)}
        >
          <CloseIcon />
        </IconButton>

        <Typography
          align="left"
          sx={{
            fontWeight: "bold",
            fontSize: "16px",
            mb: { xs: 0, sm: 1 },
            pr: 4,
            color: "white"
          }}
        >
          Código de acceso (Invitado)
        </Typography>

        <Typography
          variant="body2"
          align="left"
          sx={{ display: { xs: "none", sm: "block" }, color: "white", fontSize: "12px" }}
        >
          Por favor, ingrese el código de acceso para invitados de este curso.
        </Typography>
      </Box>

      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          backgroundColor: "white",
          p: { xs: 2, sm: 3 },
        }}
      >
        <Grid container rowSpacing={2} spacing={{ xs: 2, sm: 3 }}>
          <Grid item xs={12}>
            <Typography>
              <Chip
                label={dataDrawer?.courseDescription}
                color="primary"
                style={{ fontSize: 18 }}
              />
            </Typography>
          </Grid>

          {errorMsg && (
            <Grid item xs={12}>
              <Alert severity="error">{errorMsg}</Alert>
            </Grid>
          )}

          <Grid item xs={12}>
            <TextField
              label="Código de acceso"
              placeholder="Ej: CURSO-VERANO-2026"
              type="text"
              fullWidth
              variant="outlined"
              size="small"
              value={accessCode}
              onChange={(e) => setAccessCode(e.target.value)}
              disabled={isLoading}
              inputProps={{ maxLength: 20 }}
              helperText={`${accessCode.length}/20 caracteres. Déjalo vacío si deseas eliminar el código actual.`}
            />
          </Grid>

          <Grid item xs={12} sx={{ textAlign: "center" }}>
            {isMobile ? (
              <Stack direction="column" spacing={2} sx={{ mt: 1, mb: 2 }}>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleSave}
                  disabled={isLoading}
                >
                  {isLoading && <Spinner color="primary" />}
                  Guardar
                </Button>
                <Button
                  fullWidth
                  variant="outlined"
                  color="error"
                  disabled={isLoading}
                  onClick={() => setOpenDrawer(false)}
                >
                  Cancelar
                </Button>
              </Stack>
            ) : (
              <Box sx={{ mt: 2, mb: 2, display: "inline-block" }}>
                <Button
                  variant="contained"
                  onClick={handleSave}
                  disabled={isLoading}
                  sx={{ width: 100, marginRight: 2 }}
                >
                  {isLoading && <Spinner color="primary" />}
                  Guardar
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  disabled={isLoading}
                  sx={{ border: 2, width: 100 }}
                  onClick={() => setOpenDrawer(false)}
                >
                  Cancelar
                </Button>
              </Box>
            )}
          </Grid>
        </Grid>
      </Box>
    </Drawer>
  );
};

export default AccessCodeDrawer;
